import React, { useCallback, useEffect, useRef, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Editor } from 'primereact/editor';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import GlobalTooltip from 'components/GlobalTooltip';
import debounce from 'lodash/debounce';

import ImageComponent from 'shared/ImageComponent';
import { SentimentDot } from 'components/SentimentDot/SentimentDot';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { useMessageContext } from '../../context';
import { useCancelScheduledMessage } from '../../hooks/useMessageMutations';
import { getSignedUrl } from '../../services/messageService';
import { useNavbarContext } from 'context/NavbarContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { newlinesToBrForDisplay } from 'utils/utility_functions/convertToHtml';
import { getTenantDetails, openLinkInBlank, replaceLawftDate, sanitizeHtmlColorContent } from 'utils/utils';
import { generateFullName } from 'utils/generateFullNameUtils';
import constants from 'constants/index';
import './ConversationList.scss';

// ── Constants ─────────────────────────────────────────────────────────────
const MAX_VISIBLE_READERS = 5;

// ── Helpers ───────────────────────────────────────────────────────────────

function buildReaderTooltip(reader, timezone, format) {
  const name = `${reader.first_name || ''} ${reader.last_name || ''}`.trim() || reader.email;
  const formattedTime = reader.last_read_at ? convertDateFromApiToUserTimezone(reader.last_read_at, timezone, format) : '';
  return [name, formattedTime ? `Read: ${formattedTime}` : ''].filter(Boolean).join('\n');
}

function ReadersAvatarStack({ readers, timezone, format }) {
  if (!readers?.length) return null;
  const [tenantId] = getTenantDetails();

  const visible = readers.slice(0, MAX_VISIBLE_READERS);
  const overflowCount = readers.length - visible.length;

  // Plain-text list of hidden readers shown on the overflow "+N" badge
  const overflowTip = readers
    .slice(MAX_VISIBLE_READERS)
    .map((r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email)
    .join(', ');

  return (
    <div className="msg-readers-row">
      <span className="msg-readers-label">
        <i className="pi pi-check-circle" />
        Read
      </span>

      <div className="msg-reader-stack">
        {/* Overflow "+N" badge — first in DOM, leftmost because of row-reverse */}
        {overflowCount > 0 && (
          <GlobalTooltip
            tooltip={overflowTip}
            position="top"
            className="msg-reader-avatar msg-reader-avatar--overflow"
            aria-label={`${overflowCount} more readers`}
          >
            +{overflowCount}
          </GlobalTooltip>
        )}

        {/* Visible avatars — reversed so the first reader sits on top */}
        {[...visible].reverse().map((reader) => {
          const tooltipHtml = buildReaderTooltip(reader, timezone, format);
          const userImage = `${tenantId}/${reader.user_id}/profile_image`;

          return (
            <GlobalTooltip
              key={reader.user_id}
              tooltip={tooltipHtml}
              position="top"
              aria-label={`${reader.first_name || ''} ${reader.last_name || ''} read this message`}
            >
              <ImageComponent
                filePath={userImage}
                fileName={reader.first_name}
                fileSize="medium.jpg"
                avatarSize="medium"
                className="msg-reader-avatar"
              />
            </GlobalTooltip>
          );
        })}
      </div>
    </div>
  );
}

function ConversationList({ messageDetails, hasNextPage, isFetchingNextPage, fetchNextPage }) {
  const scrollContainerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const history = useHistory();
  const { addToast } = useToast();
  const userContext = useUserDetailsContext();
  const { isFromClientPortal } = useMessageContext();
  const { downloadFile } = useNavbarContext();
  const cancelScheduledMutation = useCancelScheduledMessage();

  // Reverse messages so latest is at bottom
  const sortedMessages = useMemo(() => {
    if (!messageDetails?.message) return [];
    return [...messageDetails.message].reverse();
  }, [messageDetails?.message]);

  const handleDownloadAttachment = useCallback(async (e, fileInfo) => {
    e.preventDefault();
    const link = await getSignedUrl(fileInfo);
    downloadFile(link, fileInfo.display_name);
  }, []);

  const navigateToUser = useCallback(
    (item) => {
      if (item?.group_id) return '/settings/groups';
      if (!isFromClientPortal && item?.access_level) {
        if (item.access_level === 'client') return item?.contact_id ? `/contacts/${item.contact_id}` : '/contacts';
        return item?.firm_user_id ? `/settings/team-management/${item.firm_user_id}` : '/settings/team-management';
      }
      return '/';
    },
    [isFromClientPortal]
  );

  const handleCancelScheduled = useCallback((msg) => {
    confirmDialog({
      message: 'Are you sure you want to cancel this scheduled message?',
      header: 'Cancel Scheduled Message',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        cancelScheduledMutation.mutate(
          { messagePk: msg?.pk, messageSk: msg?.sk, messageThreadId: msg?.message_thread_id },
          {
            onSuccess: () => {
              addToast(
                false,
                toastConstant.toasterType.SUCCESS,
                toastConstant.api.SUCCESS,
                toastConstant.message.SECURE_MESSAGE_CANCEL_SCHEDULED_MESSAGE_SUCCESS
              );
            },
            onError: () => {
              addToast(
                false,
                toastConstant.toasterType.ERROR,
                toastConstant.api.FAILED,
                toastConstant.message.SECURE_MESSAGE_CANCEL_SCHEDULED_MESSAGE_FAILED
              );
            },
          }
        );
      },
      reject: () => {
        return;
      },
    });
  }, []);

  const taskAndEventNavigation = useCallback(
    (e, item, type) => {
      e.preventDefault();
      let route = isFromClientPortal ? '/client' : '';
      route += type === 'event' ? '/calendar' : '/tasks';
      const searchQuery = encodeURIComponent(type === 'event' ? item?.sk : item?.sk?.split('#')?.[1]) || '';

      if (!openLinkInBlank(route)) {
        history.push({ pathname: route, search: searchQuery ? `?id=${searchQuery}` : '' });
      }
    },
    [isFromClientPortal]
  );

  // ── Auto-scroll to bottom on initial load and new messages ───────────────────
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !sortedMessages.length) return;

    // Double-rAF: first rAF lets React commit the DOM, second fires after
    // the browser has calculated layout — giving us the correct scrollHeight.
    const scrollToBottom = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        })
      );

    if (isInitialLoadRef.current) {
      scrollToBottom();
      isInitialLoadRef.current = false;
    } else if (!isFetchingNextPage) {
      // Only follow-scroll when the user is already near the bottom
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
      if (isNearBottom) scrollToBottom();
    }
  }, [sortedMessages.length, isFetchingNextPage]);

  // ── On-scroll pagination (load older messages when scrolling up) ─────────────
  const handleScroll = useMemo(
    () =>
      debounce(() => {
        if (!scrollContainerRef.current || !hasNextPage || isFetchingNextPage) return;

        const container = scrollContainerRef.current;
        const scrollTop = container.scrollTop;

        // Load more when scrolled near the top (to load older messages)
        if (scrollTop < 50) {
          // Store current scroll height before loading
          prevScrollHeightRef.current = container.scrollHeight;
          fetchNextPage();
        }
      }, 150),
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // ── Preserve scroll position after loading older messages ────────────────────
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !prevScrollHeightRef.current) return;

    // After fetching older messages, restore scroll position
    if (!isFetchingNextPage && prevScrollHeightRef.current > 0) {
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        const heightDifference = newScrollHeight - prevScrollHeightRef.current;

        // Adjust scroll position to maintain user's view
        container.scrollTop = heightDifference;
        prevScrollHeightRef.current = 0;
      });
    }
  }, [isFetchingNextPage, sortedMessages]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div
      ref={scrollContainerRef}
      style={{ maxHeight: '70vh', minHeight: '50vh', overflowY: 'auto' }}
      role="log"
      aria-label="Message conversation thread"
      aria-live="polite"
      className="thin-scrollbar"
    >
      <>
        {/* Load more indicator at top */}
        {isFetchingNextPage && (
          <div className="msg-load-older text-center py-2">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.875rem' }} />
            <span className="ms-2 text-muted small">Loading older messages...</span>
          </div>
        )}

        {sortedMessages.map((msg, index) => (
          <div
            key={msg?.sk || index}
            className={`row col-12 p-md-3 p-2 msg-reply-wrap mssg-reply-bgcolor msg-reply-li-color${
              msg?._optimistic ? ' msg-optimistic' : ''
            }`}
            role="article"
            aria-label={`Message from ${generateFullName(msg?.author_details) || 'Unknown'}`}
            aria-busy={msg?._optimistic ? 'true' : undefined}
          >
            {/* Left side: author + message */}
            <div className="col-md-8 col-sm-6 col-12 d-flex">
              <div className="msg-img-size">
                <Link className="avatar-wrp d-flex text-decoration-none" to={() => navigateToUser(msg?.author_details)}>
                  <ImageComponent
                    filePath={msg?.created_by_user_image}
                    fileName={generateFullName(msg?.author_details)}
                    fileSize="medium.jpg"
                    avatarSize="medium"
                    className={isFromClientPortal ? 'pe-none' : ''}
                    style={{ borderRadius: '50%' }}
                  />
                </Link>
              </div>
              <div className="w-100">
                <div className="row">
                  <div className="col-sm-12 view-message-editor">
                    <Link
                      className={`p-m-0 text-bold mb-0 text-decoration-none ${classNames({
                        'redirection-link-no-text-transform': !isFromClientPortal && Boolean(generateFullName(msg?.author_details)),
                      })}`}
                      to={() => navigateToUser(msg?.author_details)}
                    >
                      {msg?.author_details?.firm_user_id && <i className="fas fa-user-tie me-2" />}
                      {generateFullName(msg?.author_details)}
                    </Link>

                    <div className="d-flex w-100" style={{ wordBreak: 'break-word', alignItems: 'baseline' }}>
                      {msg?.message && (
                        <Editor
                          value={
                            msg.message
                              ? newlinesToBrForDisplay(
                                  sanitizeHtmlColorContent(replaceLawftDate(msg.message, userContext?.userDetails?.timezone))
                                )
                              : ' '
                          }
                          readOnly
                          className="message-reply pe-2"
                        />
                      )}
                      {msg?.sentiment_analysis?.sentiment && !isFromClientPortal && (
                        <SentimentDot tooltipPosition="top" type={msg.sentiment_analysis.sentiment} typeOnly />
                      )}
                    </div>
                  </div>

                  {/* Attachments */}
                  {((msg?.attachment?.length || 0) > 0 ||
                    (msg?.event_attachment?.length || 0) > 0 ||
                    (msg?.task_attachment?.length || 0) > 0) && (
                    <div className="col-sm-12 my-2">
                      {(msg?.attachment || []).map((file, idx) => (
                        <div key={idx} className="p-chip p-component pointer text-nowrap ps-0 pe-3">
                          <button
                            type="button"
                            className="small pointer p-chip-text btn btn-link p-0"
                            onClick={(e) => handleDownloadAttachment(e, file)}
                            aria-label={`Download attachment ${file?.file_name || 'file'}`}
                          >
                            <i className="fas fa-file-pdf" aria-hidden="true" /> {file?.file_name || 'Untitled'}
                          </button>
                        </div>
                      ))}
                      {(msg?.event_attachment || []).map((event, i) => (
                        <button
                          key={i}
                          type="button"
                          className="p-chip p-component text-break ps-0 btn btn-link"
                          style={{ marginRight: '0.5rem' }}
                          onClick={(e) => taskAndEventNavigation(e, event, 'event')}
                          aria-label={`View event ${event?.event_name || 'Untitled'}`}
                        >
                          <span className="small p-chip-text">
                            <i className="icon-calendar" aria-hidden="true" /> {event?.event_name || 'Untitled Event'}
                          </span>
                        </button>
                      ))}
                      {(msg?.task_attachment || []).map((task, i) => (
                        <button
                          key={i}
                          type="button"
                          className="p-chip p-component text-break btn btn-link"
                          style={{ marginRight: '0.5rem' }}
                          onClick={(e) => taskAndEventNavigation(e, task, 'task')}
                          aria-label={`View task ${task?.task_name || 'Untitled'}`}
                        >
                          <span className="text-muted small mb-1 p-chip-text">
                            <i className="fas fa-tasks" aria-hidden="true" /> {task?.task_name || 'Untitled Task'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right side: timestamp + viewed info */}
            <div className="col-md-4 col-sm-6 col-12 view-message-editor text-end-sm">
              {msg?._optimistic ? (
                <span className="msg-sending-badge" aria-live="polite">
                  <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                  Sending…
                </span>
              ) : msg?.scheduled_send_at && msg?.scheduled_status === 'scheduled' ? (
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <i className="far fa-clock" style={{ color: 'var(--icon-red)' }} />
                  <p className="mb-0">
                    Scheduled:{' '}
                    {convertDateFromApiToUserTimezone(
                      msg.scheduled_send_at,
                      userContext?.userDetails?.timezone,
                      constants.month_date_year_time_12_format
                    )}
                  </p>
                  <Button
                    type="button"
                    className="p-button-rounded p-button-text"
                    onClick={() => handleCancelScheduled(msg)}
                    aria-label="Cancel scheduled message"
                    icon="icon-delete"
                    tooltip="Cancel scheduled message"
                    tooltipOptions={{ position: 'top', showDelay: 100 }}
                  />
                </div>
              ) : (
                <p className="p-m-0 mb-0">
                  {convertDateFromApiToUserTimezone(msg.time, userContext?.userDetails?.timezone, constants.month_date_year_time_12_format)}
                </p>
              )}

              {!isFromClientPortal &&
                (msg?.viewed || []).map((user, i) => (
                  <React.Fragment key={i}>
                    <Link
                      className={`p-m-0 text-bold mb-0 ${classNames({ 'redirection-link-no-text-transform': Boolean(user?.viewed_by) })}`}
                      to={() => navigateToUser({ ...user, access_level: 'client' })}
                    >
                      {user?.viewed_by}
                    </Link>
                    {user.viewed_at && (
                      <p className="p-m-0 text-muted mb-0">
                        Viewed:{' '}
                        {convertDateFromApiToUserTimezone(
                          user.viewed_at,
                          userContext?.userDetails?.timezone,
                          constants.month_date_year_time_12_format
                        )}
                      </p>
                    )}
                  </React.Fragment>
                ))}

              {/* Read receipts — firm users who have read this message */}
              {!isFromClientPortal && msg?.other_readers?.length > 0 && (
                <ReadersAvatarStack
                  readers={msg.other_readers}
                  timezone={userContext?.userDetails?.timezone}
                  format={constants.month_date_year_time_12_format}
                />
              )}
            </div>
          </div>
        ))}
      </>
    </div>
  );
}

ConversationList.propTypes = {
  messageDetails: PropTypes.shape({
    message: PropTypes.arrayOf(
      PropTypes.shape({
        sk: PropTypes.string,
        message: PropTypes.string,
        created_by_details: PropTypes.object,
        created_by_user_image: PropTypes.string,
        created_by_name: PropTypes.object,
        attachment: PropTypes.array,
        event_attachment: PropTypes.array,
        task_attachment: PropTypes.array,
        sentiment_analysis: PropTypes.object,
        scheduled_send_at: PropTypes.string,
        scheduled_status: PropTypes.string,
        time: PropTypes.string,
        viewed: PropTypes.array,
        pk: PropTypes.string,
        other_readers: PropTypes.arrayOf(
          PropTypes.shape({
            email: PropTypes.string,
            user_id: PropTypes.string,
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            access_level: PropTypes.string,
            last_read_at: PropTypes.string,
          })
        ),
      })
    ),
  }),
  hasNextPage: PropTypes.bool,
  isFetchingNextPage: PropTypes.bool,
  fetchNextPage: PropTypes.func,
  totalCount: PropTypes.number,
};

ConversationList.defaultProps = {
  messageDetails: null,
  hasNextPage: false,
  isFetchingNextPage: false,
  fetchNextPage: () => {},
  totalCount: 0,
};

export default memo(ConversationList);
